import AutoSuggestSingle from 'components/shared/auto-suggest/AutoSuggestSingle';
import SimpleRadioButtons, { FancyRadioButtonOption } from '~/components/shared/SimpleRadioButtons';
import * as React from 'react';
import { Button, FormGroup } from 'reactstrap';
import { FilterOptions, FilterString } from '..';
import { AutoSuggestArrayFilterEditProps, AutoSuggestValue } from '../providers/AutoSuggestValue';
import { NotNullValue, NullValue, NullValueLabel } from '../providers/Constants';
import FilterEditRequiredValidation from './FilterEditRequiredValidation';
import {
	AnyValueAutoSuggestValue,
	FiltersListButtonGroup,
	FiltersListCloseButton,
	FiltersListAddButton,
	FiltersListValueButton,
	NoValueAutoSuggestValue,
} from './index';

interface MultipleTextFilterEditState {
	currentValue: AutoSuggestValue | null;
	displayValidationError: boolean;
	selectedValueToggle: string;
}

enum ValueToggleType {
	Specific = 'specific',
	Any = 'any',
}

const valueToggleOptions: FancyRadioButtonOption[] = [
	{ name: ValueToggleType.Specific, label: 'Specific Values', disabled: false },
	{ name: ValueToggleType.Any, label: 'Any Value', disabled: false },
];

/**
 * Configures multiple simple text values within a filter
 */
class MultipleAutoSuggestFilterEdit extends React.Component<
	AutoSuggestArrayFilterEditProps,
	MultipleTextFilterEditState
> {
	constructor(props: AutoSuggestArrayFilterEditProps) {
		super(props);

		this.state = {
			currentValue: null,
			displayValidationError: false,
			selectedValueToggle:
				Array.isArray(props.value) && props.value.findIndex((val) => val.value === NotNullValue) > -1
					? ValueToggleType.Any
					: ValueToggleType.Specific,
		};
	}

	/**
	 * Update local state when the controlled text box is updated
	 *
	 * @private
	 * @memberof MultipleTextFieldEdit
	 */
	private handleOnChange = (value: AutoSuggestValue) => {
		this.setState({ currentValue: value });
	};

	/**
	 * Returns a filtered list of values. Removes null values for non-nullable filters.
	 *
	 * @private
	 * @returns {string[]}
	 * @memberof MultipleTextFilterEdit
	 */
	private getValuesFromProps(): AutoSuggestValue[] {
		if (!this.props.value) {
			return [];
		}

		return this.props.value.filter((value) => this.props.filter.nullable || !!value);
	}

	/**
	 * Adds the value currently in state to the list of values selected.
	 *
	 * @private
	 * @memberof MultipleTextFieldEdit
	 */
	private addCurrentValue = () => {
		this.setState({
			displayValidationError: false,
		});

		if (!this.state.currentValue) {
			this.setState({
				displayValidationError: true,
			});

			return;
		}

		this.addValue(this.state.currentValue);
		this.setState({ currentValue: null });
	};

	/**
	 * Begins filtering for any value
	 *
	 * @private
	 * @memberof MultipleTextFilterEdit
	 */
	private filterByAnyValue = () => {
		this.setState({
			displayValidationError: false,
		});

		this.props.onChange([AnyValueAutoSuggestValue], false);
	};

	private addValue = (value: AutoSuggestValue) => {
		const values = this.getValuesFromProps();

		// ensure uniqueness
		if (values) {
			// let the parent component know what the new values are.
			this.props.onChange(
				[...values, value].filter((val) => val.value !== NotNullValue),
				false,
			);
		}
	};

	/**
	 * Remove a specific value from the list
	 *
	 * @private
	 * @memberof MultipleTextFieldEdit
	 */
	private removeValue = (fieldIndex: number) => {
		const values = this.getValuesFromProps();

		// let the parent component know what the new values are.
		const newValues = values.filter((_, index) => index !== fieldIndex);
		this.props.onChange(newValues, false);
	};

	/**
	 * Update state when the value toggle changes
	 *
	 * @private
	 * @memberof MultipleTextFilterEdit
	 */
	private handleValueToggleChange = (selectedName: string) => {
		this.setState({
			selectedValueToggle: selectedName,
		});
	};

	/**
	 * Handles the click after adding a filter for "no value"
	 *
	 * @private
	 * @memberof MultipleTextFilterEdit
	 */
	private handleAddNoValueFilter = (options?: FilterOptions) => {
		this.setState({
			displayValidationError: false,
		});

		if (options && options.nullValueLabel) {
			this.addValue({
				label: options.nullValueLabel,
				value: NullValue,
			});
		} else {
			this.addValue(NoValueAutoSuggestValue);
		}
	};

	public render() {
		const values = this.getValuesFromProps();
		const filterActionClass =
			'filters-popover__actions ' + (values.length > 0 ? 'filters-popover__actions--divider' : '');

		const {
			filter: { nullable, options = {} },
		} = this.props;

		const { selectedValueToggle } = this.state;

		const isDisabled = values.findIndex((val) => val.value === NullValue) !== -1;

		if (!options.params) {
			options.params = {};
		}

		const existingIds = values.map((singleValue) => singleValue.value);
		options.params.ignoredIds = existingIds;

		return (
			<FormGroup className="filters-popover__form-group">
				{nullable ? (
					<>
						<SimpleRadioButtons
							name="valueToggle"
							value={selectedValueToggle}
							onChange={this.handleValueToggleChange}
							options={valueToggleOptions}
						/>
					</>
				) : null}

				{selectedValueToggle === ValueToggleType.Specific ? (
					<>
						<hr />
						<AutoSuggestSingle
							name="AutoSuggestFilter"
							label="Auto Suggest Filter"
							value={this.state.currentValue}
							onChange={this.handleOnChange}
							autoSuggestOptions={{
								allowNewValues: false,
								baseUrl: options && options.baseUrl,
								params: options && options.params,
							}}
						/>

						{nullable ? (
							<>
								<Button
									className={
										isDisabled
											? 'fitlers-popover__btn-link fitlers-popover__btn-link--disabled'
											: 'fitlers-popover__btn-link'
									}
									type="button"
									color="secondary"
									onClick={() => this.handleAddNoValueFilter(options)}
									disabled={isDisabled}
									data-testid="filters-null-value-button"
								>
									+ Add {options && options.nullValueLabel ? options.nullValueLabel : NullValueLabel}{' '}
									Filter {isDisabled ? <span>(Disabled)</span> : null}
								</Button>{' '}
							</>
						) : null}

						<div className={filterActionClass}>
							<FiltersListAddButton onClick={this.addCurrentValue} />
						</div>

						{values
							.filter((value) => value.value !== NotNullValue)
							.map((value, index) => {
								return (
									<React.Fragment key={index}>
										<FiltersListButtonGroup>
											<FiltersListValueButton>
												{FilterString(value.label, options)}
											</FiltersListValueButton>
											<FiltersListCloseButton
												ariaLabel={`Remove ${value.label} as a filter`}
												onClick={() => {
													this.removeValue(index);
												}}
											/>
										</FiltersListButtonGroup>{' '}
									</React.Fragment>
								);
							})}
						{this.state.displayValidationError ? <FilterEditRequiredValidation /> : null}
					</>
				) : (
					<div className={filterActionClass}>
						<FiltersListAddButton onClick={this.filterByAnyValue} />
					</div>
				)}
			</FormGroup>
		);
	}
}

export default MultipleAutoSuggestFilterEdit;
